/**
 * Module dependencies.
 */

import { Button, Subtitle, Type } from 'src/components/core';
import { NextPage } from 'next/types';
import { color, media, units } from '@untile/react-components';
import { supportPrograms } from 'src/core/content-config/support-programs';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Container from 'src/components/core/layout/container';
import Item from 'src/components/downloads/download-item';
import Metatags from 'src/components/core/metatags';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: ${color('backgrounds.taupe01')};
  padding: ${units(14)} 0 ${units(8)};
  position: relative;

  ${media.min('md')`
    padding: ${units(20)} 0 ${units(18)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title    .'
    '. subtitle .'
    '. button   .'
    '. files   .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-areas:
      '. title    title    title  .'
      '. subtitle subtitle button .'
      '. .        files    .      .';
    grid-template-columns: 1fr 2fr 6fr 2fr 1fr;
    grid-template-rows: repeat(3, max-content);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Subtitle)`
  grid-area: subtitle;

  ${media.max('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  grid-area: button;

  ${media.min('md')`
    align-self: flex-end;
    display: flex;
    justify-self: flex-end;
  `}
`;

/**
 * `FilesWrapper` styled component.
 */

const FilesWrapper = styled.div`
  grid-area: files;
`;

/**
 * `FilesCategoryWrapper` styled component.
 */

const FilesCategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 50px;

  ${media.min('md')`
    padding-top: ${units(9)};
  `}
`;

/**
 * `FilesCategory` styled component.
 */

const FilesCategory = styled(Subtitle).attrs({ size: 'large' })`
  margin-bottom: ${units(3)};

  ${media.min('md')`
    text-align: center;
  `}
`;

/**
 * `SupportPrograms` page.
 */

const SupportPrograms: NextPage = () => {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const buttonUrl = routeResolver('contactUs');
  const description = translate('support-programs:description');
  const title = translate('support-programs:title');
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <>
      <Metatags
        description={translate('support-programs:description')}
        pageTitle={translate('support-programs:title')}
        title={translate('support-programs:title')}
      />

      <Wrapper>
        <StyledContainer>
          <Grid>
            <Title {...animProps}>{title}</Title>

            <Description {...animProps}>{description}</Description>

            <ButtonWrapper {...animProps}>
              <Button
                colorTheme={'secondary'}
                href={buttonUrl}
                size={'large'}
              >
                {translate('common:actions.reachOutToUs')}
              </Button>
            </ButtonWrapper>

            <FilesWrapper>
              {map(
                sortBy(supportPrograms, 'name'),
                ({ downloads, name }, index) => (
                  <FilesCategoryWrapper key={index}>
                    <FilesCategory
                      {...animProps}
                      data-scroll-animation-delay={'150'}
                    >
                      {translate(name)}
                    </FilesCategory>

                    {map(downloads, ({ name, size, url }, downloadIndex) => (
                      <div
                        {...animProps}
                        data-scroll-animation-delay={'150'}
                        key={downloadIndex}
                      >
                        <Item
                          size={size}
                          title={name}
                          url={url}
                        />
                      </div>
                    ))}
                  </FilesCategoryWrapper>
                )
              )}
            </FilesWrapper>
          </Grid>
        </StyledContainer>
      </Wrapper>
    </>
  );
};

/**
 * Export `SupportPrograms` page.
 */

export default SupportPrograms;
